import { MinusCircleOutlined, PlusOutlined, ReloadOutlined, TrophyOutlined } from '@ant-design/icons';
import type { InquiryRow, QuoteCreateRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayDate, formatDisplayTime, formatQty, percentToRatio } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const STATUS_META: Record<string, { text: string; color: string }> = {
  QUOTING: { text: '报价中', color: 'blue' },
  COMPARING: { text: '比价中', color: 'gold' },
  AWARDED: { text: '已定标', color: 'green' },
  CANCELED: { text: '已取消', color: 'default' }
};

function MaterialSelect({ value, onChange }: { value?: string; onChange?: (value?: string) => void }) {
  const [keyword, setKeyword] = useState('');
  const options = useQuery({
    queryKey: ['inquiry', 'material', keyword],
    queryFn: () => hq.material.list({ keyword: keyword || undefined, size: 20 })
  });
  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder="搜索物资编码/名称"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={options.isPending}
      style={{ width: 260 }}
      options={(options.data?.items ?? []).map((row) => ({
        value: row.id,
        label: `${row.materialCode} ${row.materialName}`
      }))}
    />
  );
}

/** 询价比价：建询价 → 代录报价 → 横向比价 → 定标（wl:purchase:manage）。 */
export default function InquiryOps() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSupplier, setQuoteSupplier] = useState<{ id: string; name: string } | null>(null);
  const [createForm] = Form.useForm();
  const [quoteForm] = Form.useForm();

  const list = useQuery({
    queryKey: ['ops', 'inquiry', status],
    queryFn: () => hq.opsExtras.inquiry.list({ status: status || undefined, size: 50 })
  });

  const detail = useQuery({
    queryKey: ['ops', 'inquiry', 'detail', currentId],
    enabled: Boolean(currentId),
    queryFn: () => hq.opsExtras.inquiry.detail(currentId ?? '')
  });

  const suppliers = useQuery({
    queryKey: ['ops', 'inquiry', 'suppliers'],
    queryFn: () => hq.catalog.suppliers({ size: 100 }),
    staleTime: 5 * 60 * 1000
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'inquiry'] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const values = await createForm.validateFields();
      const items = (values.items ?? []).filter((item: Record<string, unknown>) => item.materialId && Number(item.qty) > 0);
      if (items.length === 0) throw new ApiError({ message: '请至少添加一条物资', code: 400 });
      return hq.opsExtras.inquiry.create({
        title: values.title,
        inquiryType: values.inquiryType ?? 'OPEN',
        deadline: values.deadline ? (values.deadline as Dayjs).toISOString() : undefined,
        remark: values.remark,
        items: items.map((item: Record<string, unknown>) => ({
          materialId: String(item.materialId),
          qty: Number(item.qty),
          refPrice: Number(item.refPrice ?? 0),
          remark: String(item.remark ?? '')
        }))
      });
    },
    onSuccess: async () => {
      message.success('询价单已创建');
      setCreateOpen(false);
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '创建失败')
  });

  const quote = useMutation({
    mutationFn: async () => {
      const values = await quoteForm.validateFields();
      const target = detail.data;
      if (!target || !quoteSupplier) throw new Error('缺少询价单');
      const body: QuoteCreateRequest = {
        supplierId: quoteSupplier.id,
        supplierName: quoteSupplier.name,
        validUntil: values.validUntil ? (values.validUntil as Dayjs).format('YYYY-MM-DD') : undefined,
        items: target.items.map((item) => ({
          inquiryItemId: item.id,
          unitPrice: Number(values[`price_${item.id}`] ?? 0),
          qty: item.qty,
          taxRate: percentToRatio(values[`tax_${item.id}`] as number | undefined),
          deliveryDays: Number(values[`days_${item.id}`] ?? 0)
        }))
      };
      return hq.opsExtras.inquiry.quote(currentId ?? '', body);
    },
    onSuccess: async (result) => {
      message.success(`报价已保存，合计 ${formatAmount(result.totalAmount)}`);
      setQuoteOpen(false);
      await refresh();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '报价失败')
  });

  const award = (quoteId: string, supplierName: string, price: number) =>
    modal.confirm({
      title: '确认定标',
      content: `定标给「${supplierName}」，报价 ${formatAmount(price)}。定标后将自动生成 CG 采购单（收货仓库取本院中心库/二级库），不可改标，其余报价自动置为未中标。`,
      okText: '定标并生成采购单',
      onOk: async () => {
        try {
          const result = await hq.opsExtras.inquiry.award(currentId ?? '', { quoteId });
          message.success(`已定标，采购单 ${result.purchaseOrderNo} 已生成`);
          await refresh();
        } catch (error) {
          message.error(error instanceof ApiError ? error.friendlyMessage : '定标失败');
          throw error;
        }
      }
    });

  const cancel = (row: InquiryRow) =>
    modal.confirm({
      title: `取消 ${row.bizNo}`,
      content: '取消后不可恢复。',
      okButtonProps: { danger: true },
      onOk: async () => {
        await hq.opsExtras.inquiry.cancel(row.id);
        message.success('已取消');
        await refresh();
      }
    });

  const columns: ColumnsType<InquiryRow> = [
    { title: '询价单号', dataIndex: 'bizNo', width: 170 },
    { title: '主题', dataIndex: 'title', width: 240, ellipsis: true },
    { title: '方式', dataIndex: 'inquiryType', width: 90, render: (value: string) => <Tag>{value === 'OPEN' ? '公开' : '邀请'}</Tag> },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => <Tag color={STATUS_META[value]?.color}>{STATUS_META[value]?.text ?? value}</Tag>
    },
    { title: '明细', dataIndex: 'itemCount', width: 80, align: 'right' },
    { title: '报价数', dataIndex: 'quoteCount', width: 90, align: 'right' },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '截止', dataIndex: 'deadline', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '采购单',
      dataIndex: 'purchaseOrderNo',
      width: 170,
      render: (value: string | null) => (value ? <Typography.Text copyable>{value}</Typography.Text> : '-')
    },
    {
      title: '操作',
      width: 170,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            比价
          </Button>
          {['QUOTING', 'COMPARING'].includes(row.status) ? (
            <Button type="link" danger onClick={() => cancel(row)}>
              取消
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          询价比价
          <Typography.Text type="secondary">建单 → 代录报价 → 横向比价 → 定标（wl:purchase:manage）</Typography.Text>
        </Space>
      }
      extra={
        <Space>
          <Segmented
            value={status}
            onChange={(value) => setStatus(value as string)}
            options={[
              { value: '', label: '全部' },
              { value: 'QUOTING', label: '报价中' },
              { value: 'COMPARING', label: '比价中' },
              { value: 'AWARDED', label: '已定标' }
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={() => list.refetch()} loading={list.isFetching}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              createForm.setFieldsValue({ inquiryType: 'OPEN', items: [{}] });
              setCreateOpen(true);
            }}
          >
            新建询价
          </Button>
        </Space>
      }
    >
      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="询价单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}
      <Table<InquiryRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={list.data?.items ?? []}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无询价单' }}
      />

      <Modal
        title="新建询价"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={create.isPending}
        width={860}
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={() => create.mutate()} preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="title" label="询价主题" rules={[{ required: true }]} style={{ width: 360 }}>
              <Input maxLength={128} placeholder="如：2026Q4 输液器集中询价" />
            </Form.Item>
            <Form.Item name="inquiryType" label="方式" style={{ width: 140 }}>
              <Select options={[{ value: 'OPEN', label: '公开' }, { value: 'INVITED', label: '邀请' }]} />
            </Form.Item>
            <Form.Item name="deadline" label="报价截止" style={{ width: 200 }}>
              <DatePicker showTime style={{ width: '100%' }} disabledDate={(date) => date.isBefore(dayjs().startOf('day'))} />
            </Form.Item>
          </Space>

          <Typography.Title level={5}>询价明细</Typography.Title>
          <Form.List name="items">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" wrap>
                    <Form.Item name={[field.name, 'materialId']} rules={[{ required: true, message: '选择物资' }]}>
                      <MaterialSelect />
                    </Form.Item>
                    <Form.Item name={[field.name, 'qty']} rules={[{ required: true, message: '数量' }]}>
                      <InputNumber min={0.001} placeholder="数量" style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'refPrice']}>
                      <InputNumber min={0} placeholder="参考单价" style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'remark']}>
                      <Input placeholder="备注" style={{ width: 180 }} maxLength={120} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                  添加物资
                </Button>
              </Space>
            )}
          </Form.List>
          <Form.Item name="remark" label="备注" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detail.data?.inquiry.bizNo ? `比价 · ${detail.data.inquiry.bizNo}` : '询价比价'}
        width={1000}
        open={Boolean(currentId)}
        onClose={() => setCurrentId(null)}
        extra={
          detail.data && ['QUOTING', 'COMPARING'].includes(detail.data.inquiry.status) ? (
            <Button
              type="primary"
              onClick={() => {
                quoteForm.resetFields();
                setQuoteSupplier(null);
                setQuoteOpen(true);
              }}
            >
              代录报价
            </Button>
          ) : null
        }
      >
        {detail.isPending ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : detail.isError ? (
          <Alert type="error" showIcon message="比价详情加载失败" description={detail.error instanceof ApiError ? detail.error.friendlyMessage : ''} />
        ) : detail.data ? (
          <>
            <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="主题" span={2}>{detail.data.inquiry.title}</Descriptions.Item>
              <Descriptions.Item label="状态">{STATUS_META[detail.data.inquiry.status]?.text ?? detail.data.inquiry.status}</Descriptions.Item>
              <Descriptions.Item label="方式">{detail.data.inquiry.inquiryType === 'OPEN' ? '公开' : '邀请'}</Descriptions.Item>
              <Descriptions.Item label="截止">
                {formatDisplayTime(detail.data.inquiry.deadline)}
              </Descriptions.Item>
              <Descriptions.Item label="报价数">{detail.data.quotes.length}</Descriptions.Item>
              <Descriptions.Item label="采购单">
                {detail.data.inquiry.purchaseOrderNo ? (
                  <Button type="link" style={{ padding: 0 }} onClick={() => navigate('/purchase')}>
                    {detail.data.inquiry.purchaseOrderNo}
                  </Button>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
            </Descriptions>

            <Typography.Title level={5}>比价矩阵</Typography.Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.data.items}
              scroll={{ x: 600 + detail.data.quotes.length * 160 }}
              columns={[
                { title: '物资', width: 260, render: (_v, row) => `${row.materialCode ?? ''} ${row.materialName ?? ''}` },
                { title: '数量', dataIndex: 'qty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                { title: '参考价', dataIndex: 'refPrice', width: 110, align: 'right', render: (v: number) => (v > 0 ? formatAmount(v) : '-') },
                ...detail.data.quotes.map((quote) => ({
                  title: (
                    <Space direction="vertical" size={0}>
                      <span>{quote.supplierName ?? quote.supplierId}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        合计 {formatAmount(quote.totalAmount)}
                      </Typography.Text>
                    </Space>
                  ),
                  key: quote.id,
                  width: 160,
                  align: 'right' as const,
                  render: (_v: unknown, row: { id: string }) => {
                    const cell = detail.data?.quoteItems.find((item) => item.quoteId === quote.id && item.inquiryItemId === row.id);
                    if (!cell) return '-';
                    const ref = detail.data?.items.find((item) => item.id === row.id)?.refPrice ?? 0;
                    const color = ref > 0 && cell.unitPrice > ref ? 'red' : ref > 0 ? 'green' : undefined;
                    return (
                      <Typography.Text type={color === 'red' ? 'danger' : color === 'green' ? 'success' : undefined}>
                        {formatAmount(cell.unitPrice)}
                      </Typography.Text>
                    );
                  }
                }))
              ]}
            />

            <Typography.Title level={5} style={{ marginTop: 16 }}>报价明细</Typography.Title>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.data.quotes}
              columns={[
                { title: '供应商', dataIndex: 'supplierName', width: 220, render: (v: string | null, row) => v || row.supplierId },
                { title: '报价合计', dataIndex: 'totalAmount', width: 140, align: 'right', render: (v: number) => formatAmount(v) },
                { title: '有效期', dataIndex: 'validUntil', width: 120, render: (v: string | null) => formatDisplayDate(v) },
                { title: '状态', dataIndex: 'status', width: 100, render: (v: string) => <Tag color={v === 'WON' ? 'green' : v === 'LOST' ? 'default' : 'blue'}>{v}</Tag> },
                { title: '报价时间', dataIndex: 'submitAt', width: 160, render: (v: string) => formatDisplayTime(v) },
                {
                  title: '操作',
                  width: 120,
                  render: (_v, row) =>
                    detail.data?.inquiry.status !== 'AWARDED' && detail.data?.inquiry.status !== 'CANCELED' ? (
                      <Button
                        type="link"
                        icon={<TrophyOutlined />}
                        onClick={() => award(row.id, row.supplierName ?? row.supplierId, row.totalAmount)}
                      >
                        定标
                      </Button>
                    ) : row.id === detail.data?.inquiry.awardedQuoteId ? (
                      <Tag color="green">中标</Tag>
                    ) : null
                }
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="代录供应商报价"
        open={quoteOpen}
        onCancel={() => setQuoteOpen(false)}
        onOk={() => quoteForm.submit()}
        confirmLoading={quote.isPending}
        width={760}
        destroyOnHidden
      >
        <Form form={quoteForm} layout="vertical" onFinish={() => quote.mutate()} preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item label="供应商" required style={{ width: 320 }}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="选择供应商"
                value={quoteSupplier?.id}
                onChange={(value) => {
                  const row = (suppliers.data?.items ?? []).find((item) => item.id === value);
                  setQuoteSupplier(row ? { id: row.id, name: row.supplierName } : null);
                  quoteForm.setFieldValue('supplierId', value);
                }}
                options={(suppliers.data?.items ?? []).map((row) => ({ value: row.id, label: row.supplierName }))}
              />
            </Form.Item>
            <Form.Item name="validUntil" label="报价有效期" style={{ width: 180 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={detail.data?.items ?? []}
            columns={[
              { title: '物资', width: 260, render: (_v, row) => `${row.materialCode ?? ''} ${row.materialName ?? ''}` },
              { title: '数量', dataIndex: 'qty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
              { title: '参考价', dataIndex: 'refPrice', width: 110, align: 'right', render: (v: number) => (v > 0 ? formatAmount(v) : '-') },
              {
                title: '报价单价',
                width: 150,
                render: (_v, row) => (
                  <Form.Item name={`price_${row.id}`} rules={[{ required: true, message: '必填' }]} style={{ marginBottom: 0 }}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                )
              },
              {
                // 全站统一：界面按百分数录入（13 表示 13%），提交时 percentToRatio 换算成后端 0~1
                title: '税率（%）',
                width: 120,
                render: (_v, row) => (
                  <Form.Item name={`tax_${row.id}`} style={{ marginBottom: 0 }}>
                    <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} placeholder="13" />
                  </Form.Item>
                )
              },
              {
                title: '交期(天)',
                width: 110,
                render: (_v, row) => (
                  <Form.Item name={`days_${row.id}`} style={{ marginBottom: 0 }}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                )
              }
            ]}
          />
        </Form>
      </Modal>
    </Card>
  );
}
